import { useMemo } from "react";
import { transformSSELogs, type UnifiedLogEntry } from "./log-transformers";
import { useGetManagedServerLogs } from "./use-get-managed-server-logs";
import { useGetManagedServerLogsHistory } from "./use-get-managed-server-logs-history";
import { useGetServerLogs } from "./use-get-server-logs";

export type TimeRangeValue = "realtime" | "15m" | "1h" | "6h" | "24h" | "7d";

export type LogEntry = UnifiedLogEntry;

type UseLogsSourceArgs = {
  serverId: string;
  isManaged: boolean;
  timeRange: TimeRangeValue;
  startTimeUtc?: string;
  endTimeUtc?: string;
};

type LogsSourceResult = {
  logs: LogEntry[];
  isLoading: boolean;
  error: Error | null;
};

const toError = (value: unknown): Error | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === "string") {
    return new Error(value);
  }
  return new Error("Unknown error");
};

export const useLogsSource = ({
  serverId,
  isManaged,
  timeRange,
  startTimeUtc,
  endTimeUtc,
}: UseLogsSourceArgs): LogsSourceResult => {
  const isRealtimeMode = timeRange === "realtime";

  const {
    data: cloudLogs,
    error: cloudError,
    isConnected: isCloudConnected,
  } = useGetServerLogs(serverId, { enabled: !isManaged });

  const {
    data: managedRealtimeLogs,
    error: managedRealtimeError,
    isConnected: isManagedConnected,
  } = useGetManagedServerLogs(serverId, {
    enabled: isManaged && isRealtimeMode,
  });

  const {
    data: managedHistoricalLogs,
    error: managedHistoricalError,
    isPending: isManagedHistoricalPending,
  } = useGetManagedServerLogsHistory({
    serverId,
    startTimeUtc,
    endTimeUtc,
    enabled: isManaged && !isRealtimeMode,
  });

  return useMemo((): LogsSourceResult => {
    // The connecting spinner is only for the initial connect — before any logs
    // arrive. Once we have logs (or an error), keep showing them even after the
    // stream closes; a closed stream must not replace buffered output with a
    // spinner.
    if (!isManaged) {
      const logs = cloudLogs ? transformSSELogs(cloudLogs) : [];
      const error = cloudError;
      const isLoading = !(error || isCloudConnected || logs.length > 0);
      return { logs, error, isLoading };
    }

    if (isRealtimeMode) {
      const logs = managedRealtimeLogs
        ? transformSSELogs(managedRealtimeLogs)
        : [];
      const error = managedRealtimeError;
      const isLoading = !(error || isManagedConnected || logs.length > 0);
      return { logs, error, isLoading };
    }

    const logs = managedHistoricalLogs ?? [];
    const error = toError(managedHistoricalError);
    const isLoading = Boolean(isManagedHistoricalPending);
    return { logs, error, isLoading };
  }, [
    cloudLogs,
    cloudError,
    isCloudConnected,
    isManaged,
    isManagedConnected,
    isManagedHistoricalPending,
    isRealtimeMode,
    managedHistoricalError,
    managedHistoricalLogs,
    managedRealtimeError,
    managedRealtimeLogs,
  ]);
};
